import { useMemo, useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import {
  usePartnerStatementLeads,
  useReferralPartnerTotals,
  useReferralPayouts,
  type ReferralPartner,
} from '@/hooks/useReferralPartners';
import {
  buildPartnerStatement,
  describeDealForPartner,
  feeStatusLabel,
  generatePartnerStatementHtml,
  isPercentBasis,
  outcomeText,
  partnerStatementCsv,
  shareIncTax,
  showsCosts,
} from '@/utils/partnerStatement';
import { downloadHtmlReport } from '@/utils/techCommissionHtmlGenerator';
import { formatDateInputInTz, parseDateInTz } from '@/utils/timezone';

type Preset = 'this_month' | 'last_month' | 'since_start';

const PRESETS: { value: Preset; label: string }[] = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'since_start', label: 'Since the deal started' },
];

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function presetRange(preset: Preset, today: string, dealStart: string): { from: string; to: string } {
  if (preset === 'since_start') return { from: dealStart, to: today };
  const monthStart = `${today.slice(0, 7)}-01`;
  if (preset === 'this_month') return { from: monthStart, to: today };
  const lastEnd = addDays(monthStart, -1);
  return { from: `${lastEnd.slice(0, 7)}-01`, to: lastEnd };
}

/**
 * The partner's statement for a period, the way the technician report works:
 * preview it here, then download it (open or print to PDF) and send it.
 */
export function PartnerStatementDialog({ partner, open, onOpenChange }: {
  partner: ReferralPartner;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { formatMoney, formatShortDate, timezone } = useCompanyLocale();
  const { data: company } = useCompanyProfile();
  const tax = useCompanyTax();
  const today = formatDateInputInTz(new Date(), timezone);
  const initial = presetRange('this_month', today, partner.fee_start_date);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);

  const valid = !!from && !!to && from <= to;
  const startIso = valid ? parseDateInTz(from, timezone).toISOString() : '';
  const endIso = valid ? parseDateInTz(addDays(to, 1), timezone).toISOString() : '';
  const { data: leads = [], isLoading, error } = usePartnerStatementLeads(open && valid ? partner : null, startIso, endIso);
  const { data: totals } = useReferralPartnerTotals();
  const { data: payouts = [] } = useReferralPayouts(open ? partner.id : null);

  const statement = useMemo(() => buildPartnerStatement(partner, leads), [partner, leads]);
  const t = statement.totals;
  const account = totals?.[partner.id];
  const lastPayout = payouts[0];
  const taxLabel = tax.enabled ? tax.label : '';
  const pct = isPercentBasis(partner.fee_basis);
  const costs = showsCosts(partner.fee_basis);
  const periodLabel = `${formatShortDate(from)} to ${formatShortDate(to)}`;
  const fileStem = `${partner.name.replace(/[^\w-]+/g, '_')}_statement_${from}_to_${to}`;

  const download = () => {
    const html = generatePartnerStatementHtml(statement, {
      companyName: company?.trading_name || company?.name || 'Our company',
      logoUrl: company?.logo_url ?? null,
      primaryColor: company?.primary_color ?? null,
      periodLabel,
      generatedLabel: formatShortDate(new Date()),
      taxLabel,
      taxRate: tax.enabled ? tax.rate : undefined,
      money: formatMoney,
      formatDate: formatShortDate,
      account: {
        owedNow: Number(account?.payable ?? 0),
        paidToDate: Number(account?.paid ?? 0),
        lastPayout: lastPayout ? `${formatMoney(Number(lastPayout.amount))} on ${formatShortDate(lastPayout.paid_on)}` : null,
      },
    });
    downloadHtmlReport(html, `${fileStem}.html`);
    toast.success(`Statement for ${partner.name} downloaded. Open it to print or save as PDF.`);
  };

  const exportCsv = () => {
    const blob = new Blob([partnerStatementCsv(statement, formatShortDate)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileStem}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stat = (label: string, value: string, strong = false) => (
    <div className={strong ? 'rounded-lg border border-primary/30 bg-primary/5 p-3' : 'rounded-lg border p-3'}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={strong ? 'text-lg font-semibold text-primary' : 'text-lg font-semibold'}>{value}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Statement for {partner.name}</DialogTitle>
          <DialogDescription>{describeDealForPartner(partner, formatMoney, taxLabel)}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <Button key={p.value} type="button" variant="outline" size="sm"
                onClick={() => { const r = presetRange(p.value, today, partner.fee_start_date); setFrom(r.from); setTo(r.to); }}>
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="ps-from" className="text-xs">From</Label>
              <Input id="ps-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ps-to" className="text-xs">To</Label>
              <Input id="ps-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" />
            </div>
          </div>
        </div>

        {!valid ? (
          <p className="text-sm text-destructive">Pick a start date on or before the end date.</p>
        ) : error ? (
          <p className="text-sm text-destructive">Could not load the statement: {error instanceof Error ? error.message : 'unknown error'}</p>
        ) : isLoading ? (
          <div className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-40 w-full" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {stat('Leads sent', String(t.leads))}
              {stat('Jobs completed', String(t.completed))}
              {stat('Booked / in progress', String(t.booked))}
              {stat('Quoted / being arranged', String(t.quoted))}
              {stat('Not booked / cancelled', String(t.notBooked))}
              {stat(`Sales${taxLabel ? ` (inc ${taxLabel})` : ''}`, formatMoney(t.sales))}
              {pct && partner.fee_basis !== 'percent_revenue' ? stat('Profit', formatMoney(t.base)) : stat(`Sales${taxLabel ? ` (ex ${taxLabel})` : ''}`, formatMoney(t.salesExTax))}
              {tax.enabled
                ? <>
                    {stat(`${partner.name}'s share (ex ${taxLabel})`, formatMoney(t.share), true)}
                    {stat(`${partner.name}'s share (inc ${taxLabel})`, formatMoney(shareIncTax(t.share, tax.rate)), true)}
                  </>
                : stat(`${partner.name}'s share`, formatMoney(t.share), true)}
              {stat('Owed now (all time)', formatMoney(Number(account?.payable ?? 0)), true)}
            </div>
            <p className="text-xs text-muted-foreground">
              This period: {formatMoney(t.paid)} paid, {formatMoney(t.owed)} owed
              {t.waiting > 0 ? `, ${formatMoney(t.waiting)} due once customers pay` : ''}.
              {lastPayout ? ` Last payment ${formatMoney(Number(lastPayout.amount))} on ${formatShortDate(lastPayout.paid_on)}.` : ''}
            </p>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sent</TableHead>
                    <TableHead>Ref</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead className="text-right">Sale</TableHead>
                    {costs && <TableHead className="text-right">Costs</TableHead>}
                    {pct && <TableHead className="text-right">{partner.fee_basis === 'percent_revenue' ? 'Sale ex tax' : 'Profit'}</TableHead>}
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No leads from {partner.name} in this period.</TableCell></TableRow>
                  ) : statement.rows.map((r) => (
                    <TableRow key={r.leadNumber} className={r.outcome === 'completed' ? '' : 'text-muted-foreground'}>
                      <TableCell className="whitespace-nowrap">{formatShortDate(r.sentOn)}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.jobNumber ?? r.leadNumber}</TableCell>
                      <TableCell>{r.customer}</TableCell>
                      <TableCell>{outcomeText(r, formatShortDate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.saleIncTax != null ? formatMoney(r.saleIncTax) : '—'}</TableCell>
                      {costs && <TableCell className="text-right tabular-nums">{r.costs != null ? formatMoney(r.costs) : '—'}</TableCell>}
                      {pct && <TableCell className="text-right tabular-nums">{r.base != null ? formatMoney(r.base) : '—'}</TableCell>}
                      <TableCell className="text-right font-medium tabular-nums">{r.fee != null ? formatMoney(r.fee) : '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{feeStatusLabel(r, formatShortDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={!valid || isLoading || !!error}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button onClick={download} disabled={!valid || isLoading || !!error}>
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download statement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
